/**
 * @pt/api-client — the transport-agnostic API client.
 *
 * OWNER: app-architect. Read-only to the six engineers; a signature change is
 * arbitrated. Adding a method for an endpoint already in `ENDPOINTS` is routine
 * and does not need arbitration.
 *
 * D-025 boundary 2, "zero forks": web and native consume the SAME versioned API
 * through the SAME client. The two platforms differ in exactly two ways and both
 * are injected, not branched on:
 *
 *   1. `fetchImpl`  — web passes the browser `fetch`; native passes React
 *                     Native's `fetch`; tests pass a stub. This module never
 *                     touches a global.
 *   2. `auth`       — web uses an httpOnly cookie (`credentials: 'include'`,
 *                     no header); native uses a bearer token from the SAME
 *                     endpoint family. `SessionResponse.token` is null on web
 *                     and a string on native; nothing else differs.
 *
 * If you ever need `typeof window !== 'undefined'` in here, the design is wrong.
 */

import {
  API_PREFIX,
  endpoint,
  type ConfigEvalRequest,
  type ConfigEvalResponse,
  type EndpointSpec,
  type FabricListQuery,
  type HomeResponse,
  type MeasurementListResponse,
  type PlaceOrderRequest,
  type PlaceOrderResponse,
  type PromiseRequest,
  type RejectionBody,
  type SessionResponse,
  type SignInRequest,
  type StockMoveRequest,
  type TransitionRequest,
  type TransitionResponse,
  type CreateMeasurementRecordRequest,
} from '@pt/core';

export type FetchImpl = (
  input: string,
  init: {
    method: string;
    headers: Record<string, string>;
    body?: string;
    credentials?: 'include' | 'omit';
    signal?: unknown;
  },
) => Promise<{
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
  text: () => Promise<string>;
}>;

/**
 * How this consumer carries its session.
 *  - `cookie`: the server sets an httpOnly cookie; the client sends credentials.
 *  - `bearer`: the client holds a token and sets Authorization. Native only.
 */
export type AuthMode =
  | { readonly kind: 'cookie' }
  | { readonly kind: 'bearer'; readonly getToken: () => string | null; readonly setToken: (t: string | null) => void };

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly fetchImpl: FetchImpl;
  readonly auth: AuthMode;
  /** Called on every refusal so the caller can render human copy and log the code ([T-73]). */
  readonly onRejection?: (rejection: ApiRejection) => void;
}

/** A refusal carrying the server's machine code, violated ids, and human copy. */
export class ApiRejection extends Error {
  readonly status: number;
  readonly body: RejectionBody;
  constructor(status: number, body: RejectionBody) {
    super(body.message);
    this.name = 'ApiRejection';
    this.status = status;
    this.body = body;
  }
}

/** Thrown when the request never reached the atelier. Drives ST-OFFLINE. */
export class ApiUnreachable extends Error {
  constructor(cause: unknown) {
    super('The atelier could not be reached.');
    this.name = 'ApiUnreachable';
    this.cause = cause;
  }
}

export function createApiClient(options: ApiClientOptions) {
  const { baseUrl, fetchImpl, auth, onRejection } = options;

  async function call<T>(
    spec: EndpointSpec,
    opts: {
      params?: Record<string, string>;
      query?: Record<string, unknown>;
      body?: unknown;
    } = {},
  ): Promise<T> {
    let path = spec.path;
    for (const [key, value] of Object.entries(opts.params ?? {})) {
      path = path.replace(`:${key}`, encodeURIComponent(value));
    }
    if (path.includes(':')) {
      throw new Error(`Unresolved path parameter in ${spec.id}: ${path}`);
    }

    // PORTABILITY, not style. React Native ships a partial `URLSearchParams`
    // (whatwg-url-without-unicode): `.size` is absent and `.set` is unreliable,
    // so the previous form produced `?undefined` on native and a correct query
    // string on web — the same client behaving differently on two platforms,
    // which is the fork D-025 boundary 2 exists to prevent. Surfaced by the
    // harness; fixed here at source with the one construction every runtime
    // agrees on. Keys are sorted so a URL is stable across engines, which also
    // makes a request loggable and comparable in a test.
    const pairs: string[] = [];
    for (const key of Object.keys(opts.query ?? {}).sort()) {
      const value = (opts.query ?? {})[key];
      if (value === undefined || value === null || value === '') continue;
      pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
    const url = `${baseUrl}${API_PREFIX}${path}${pairs.length > 0 ? `?${pairs.join('&')}` : ''}`;

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth.kind === 'bearer') {
      // THE CLIENT ASKS FOR THE TRANSPORT IT WANTS (ARCHITECTURE §8; the server
      // reads this in `middleware/cookies.ts -> requestedTransport` and never
      // sniffs a user agent). Without it the server defaults to `cookie`, mints
      // a Set-Cookie no native consumer can hold, and returns `token: null` —
      // so `signIn` below never calls `auth.setToken` and every subsequent
      // request is unauthenticated. Surfaced by the D-025 harness; fixed here,
      // at the source, because it is exactly the transport injection this
      // module already promises in its own header (D-025 boundary 2,
      // RULING-006 condition i).
      headers['X-PT-Transport'] = 'bearer';
      const token = auth.getToken();
      if (token !== null) headers.Authorization = `Bearer ${token}`;
    }

    let response: Awaited<ReturnType<FetchImpl>>;
    try {
      response = await fetchImpl(url, {
        method: spec.method,
        headers,
        ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
        ...(auth.kind === 'cookie' ? { credentials: 'include' as const } : {}),
      });
    } catch (cause) {
      throw new ApiUnreachable(cause);
    }

    if (response.status === 204) return undefined as T;

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const body = (payload ?? {
        code: 'MALFORMED_REQUEST',
        violated: [],
        copyKey: 'error.unknown',
        message: 'The atelier refused that.',
        field: null,
      }) as RejectionBody;
      const rejection = new ApiRejection(response.status, body);
      onRejection?.(rejection);
      throw rejection;
    }

    return payload as T;
  }

  return {
    /** Exposed so a caller can build a URL for a print view without re-deriving the prefix. */
    urlFor(id: string, params: Record<string, string> = {}): string {
      const spec = endpoint(id);
      let path = spec.path;
      for (const [k, v] of Object.entries(params)) path = path.replace(`:${k}`, encodeURIComponent(v));
      return `${baseUrl}${API_PREFIX}${path}`;
    },

    /* ---------------------------------------------------------- session */
    async signIn(body: SignInRequest): Promise<SessionResponse> {
      const session = await call<SessionResponse>(endpoint('API-SESSION-SIGNIN'), { body });
      if (auth.kind === 'bearer' && session.token !== null) auth.setToken(session.token);
      return session;
    },
    currentSession: () => call<SessionResponse>(endpoint('API-SESSION-CURRENT')),
    async signOut(): Promise<void> {
      await call<void>(endpoint('API-SESSION-SIGNOUT'));
      if (auth.kind === 'bearer') auth.setToken(null);
    },
    requestPasswordReset: (identifier: string) =>
      call<{ recorded: true }>(endpoint('API-PASSWORD-RESET'), { body: { identifier } }),

    /* ----------------------------------------------------- client rooms */
    home: () => call<HomeResponse>(endpoint('API-HOME')),
    measurements: () => call<MeasurementListResponse>(endpoint('API-MEAS-LIST')),
    measurementRecord: (recordId: string) =>
      call<unknown>(endpoint('API-MEAS-GET'), { params: { recordId } }),
    requestRemeasure: (body: { preferredTiming?: string }) =>
      call<unknown>(endpoint('API-REMEASURE-CREATE'), { body }),
    privacyRequest: (body: { kind: 'export' | 'erasure' }) =>
      call<unknown>(endpoint('API-PRIVACY-REQUEST'), { body }),

    /* ---------------------------------------------------------- the book */
    fabrics: (query: FabricListQuery = {}) =>
      call<unknown>(endpoint('API-FABRICS-LIST'), { query: query as Record<string, unknown> }),
    fabric: (clothId: string, query: { draftId?: string } = {}) =>
      call<unknown>(endpoint('API-FABRIC-GET'), { params: { clothId }, query }),
    requestSwatch: (body: { clothId: string; address?: string }) =>
      call<unknown>(endpoint('API-SWATCH-CREATE'), { body }),
    registerClothInterest: (body: { clothId: string }) =>
      call<unknown>(endpoint('API-CLOTH-INTEREST-CREATE'), { body }),

    /* --------------------------------------------------- the commission */
    createDraft: (body: { commissionType?: string; clothId?: string }) =>
      call<{ draftId: string }>(endpoint('API-DRAFT-CREATE'), { body }),
    draft: (draftId: string) => call<unknown>(endpoint('API-DRAFT-GET'), { params: { draftId } }),
    deleteDraft: (draftId: string) =>
      call<void>(endpoint('API-DRAFT-DELETE'), { params: { draftId } }),
    evaluate: (draftId: string, body: ConfigEvalRequest) =>
      call<ConfigEvalResponse>(endpoint('API-CONFIG-EVAL'), { params: { draftId }, body }),
    review: (draftId: string) => call<unknown>(endpoint('API-REVIEW-GET'), { params: { draftId } }),
    placeOrder: (body: PlaceOrderRequest) =>
      call<PlaceOrderResponse>(endpoint('API-ORDER-PLACE'), { body }),
    orders: () => call<unknown>(endpoint('API-ORDER-LIST')),
    order: (orderRef: string) => call<unknown>(endpoint('API-ORDER-GET'), { params: { orderRef } }),
    askAboutOrder: (orderRef: string, body: { kind: 'change' | 'cancel'; message: string }) =>
      call<unknown>(endpoint('API-ORDER-REQUEST-CREATE'), { params: { orderRef }, body }),

    /* --------------------------------------------------------- atelier */
    atelierOrders: (query: Record<string, unknown> = {}) =>
      call<unknown>(endpoint('API-ATELIER-ORDERS-LIST'), { query }),
    atelierOrder: (orderRef: string) =>
      call<unknown>(endpoint('API-ATELIER-ORDER-GET'), { params: { orderRef } }),
    transition: (orderId: string, body: TransitionRequest) =>
      call<TransitionResponse>(endpoint('API-ATELIER-TRANSITION'), { params: { orderId }, body }),
    addNote: (orderId: string, body: { note: string; visibility: 'internal' | 'client_visible' }) =>
      call<unknown>(endpoint('API-ATELIER-NOTE'), { params: { orderId }, body }),
    assign: (orderId: string, body: { assignedCutter: string | null }) =>
      call<unknown>(endpoint('API-ATELIER-ASSIGN'), { params: { orderId }, body }),
    promise: (orderId: string, body: PromiseRequest) =>
      call<unknown>(endpoint('API-ATELIER-REPROMISE'), { params: { orderId }, body }),
    recordCutSheetPrint: (orderId: string, body: { format: string }) =>
      call<unknown>(endpoint('API-ATELIER-CUTSHEET-PRINT'), { params: { orderId }, body }),
    atelierClients: (query: { search?: string } = {}) =>
      call<unknown>(endpoint('API-ATELIER-CLIENTS-LIST'), { query }),
    atelierClient: (clientId: string) =>
      call<unknown>(endpoint('API-ATELIER-CLIENTS-GET'), { params: { clientId } }),

    /* ---------------------------------------------- the site's commissions
     * WIRE-1. The intake itself (API-LEAD-CREATE) is the site's server's, with
     * a key — no method here, on purpose: a browser never delivers a lead. */
    leads: (query: { status?: string } = {}) => call<unknown>(endpoint('API-ATELIER-LEADS-LIST'), { query }),
    lead: (leadId: string) => call<unknown>(endpoint('API-ATELIER-LEAD-GET'), { params: { leadId } }),
    leadToClient: (leadId: string, body: { clientId?: string }) =>
      call<unknown>(endpoint('API-ATELIER-LEAD-CLIENT'), { params: { leadId }, body }),
    closeLead: (leadId: string, body: { reason: string }) =>
      call<unknown>(endpoint('API-ATELIER-LEAD-CLOSE'), { params: { leadId }, body }),

    /* ------------------------------------------- the record, on the road
     * P-CLIENT-01 / P-PHOTO-01 / P-PAY-01 / P-ORDER-STAFF-01 / P-THAI-01.
     * Every write below takes an OPTIONAL `idempotencyKey` and the caller is
     * expected to send one: the device holding this is on a hotel connection,
     * and a second tap must not make a second client, a second record or a
     * second deposit (P-ROAD-IDEM-01). The placement is the one that REQUIRES
     * it, because it is the most expensive write in the product. */
    createClient: (body: {
      displayName: string;
      email?: string;
      deliveryAddress?: string;
      contact?: string;
      idempotencyKey?: string;
    }) => call<unknown>(endpoint('API-ATELIER-CLIENT-CREATE'), { body }),
    amendClient: (
      clientId: string,
      body: { field: string; value: string; reason?: string },
    ) => call<unknown>(endpoint('API-ATELIER-CLIENT-AMEND'), { params: { clientId }, body }),
    addClientNote: (clientId: string, body: { body: string }) =>
      call<unknown>(endpoint('API-ATELIER-CLIENT-NOTE'), { params: { clientId }, body }),
    placeOrderForClient: (
      clientId: string,
      body: {
        configuration: Record<string, unknown>;
        acknowledgements: readonly string[];
        fittingPreference: string;
        deliveryAddress?: string;
        contact?: string;
        clientNote?: string;
        overrides?: readonly { constraintId: string; reason: string }[];
        idempotencyKey: string;
      },
    ) => call<unknown>(endpoint('API-ATELIER-ORDER-PLACE'), { params: { clientId }, body }),

    /* --------------------------------------------------- the photographs */
    recordConsent: (
      clientId: string,
      body: { purpose?: string; statementText: string; statementVersion?: number },
    ) => call<unknown>(endpoint('API-ATELIER-CONSENT-RECORD'), { params: { clientId }, body }),
    photographSets: (clientId: string) =>
      call<unknown>(endpoint('API-ATELIER-PHOTOSET-LIST'), { params: { clientId } }),
    createPhotographSet: (
      clientId: string,
      body: {
        takenAt?: string;
        takenAtPlace?: string;
        measurementRecordId?: string;
        note?: string;
        photographs: readonly { pose: string; dataBase64: string }[];
        consent?: { statementText: string; statementVersion?: number };
        idempotencyKey?: string;
      },
    ) => call<unknown>(endpoint('API-ATELIER-PHOTOSET-CREATE'), { params: { clientId }, body }),

    /* -------------------------------------------------------- the money */
    paymentMethods: () => call<unknown>(endpoint('API-ATELIER-PAYMENT-METHODS')),
    payments: (orderId: string) =>
      call<unknown>(endpoint('API-ATELIER-PAYMENTS-LIST'), { params: { orderId } }),
    createPayment: (
      orderId: string,
      body: { purpose: string; amountMinor: number; processor: string; idempotencyKey?: string },
    ) => call<unknown>(endpoint('API-ATELIER-PAYMENT-CREATE'), { params: { orderId }, body }),
    recordPaymentEvent: (
      paymentIntentId: string,
      body: { kind: string; amountMinor?: number; note?: string; idempotencyKey?: string },
    ) => call<unknown>(endpoint('API-ATELIER-PAYMENT-EVENT'), { params: { paymentIntentId }, body }),

    /* ------------------------------------------------- the words, and time */
    glossary: (query: { status?: string } = {}) =>
      call<unknown>(endpoint('API-ATELIER-GLOSSARY-LIST'), { query }),
    signGlossaryTerm: (termKey: string, body: { th: string; note?: string }) =>
      call<unknown>(endpoint('API-ATELIER-GLOSSARY-SIGN'), { params: { termKey }, body }),
    /* LAW 59 · the cut tickets of one order, and Paul's hand on one, appended. */
    cutTickets: (orderRef: string) => call<unknown>(endpoint('API-ATELIER-TICKETS-GET'), { params: { orderRef } }),
    saveTicketSketch: (orderId: string, garment: string, body: unknown) =>
      call<unknown>(endpoint('API-ATELIER-TICKET-SKETCH-CREATE'), { params: { orderId, garment }, body }),
    retention: () => call<unknown>(endpoint('API-ATELIER-RETENTION')),
    atelierMeasurementRecords: (clientId: string) =>
      call<unknown>(endpoint('API-ATELIER-MEAS-LIST'), { params: { clientId } }),
    createMeasurementRecord: (clientId: string, body: CreateMeasurementRecordRequest) =>
      call<unknown>(endpoint('API-ATELIER-MEAS-CREATE'), { params: { clientId }, body }),
    lockMeasurementRecord: (recordId: string) =>
      call<unknown>(endpoint('API-ATELIER-MEAS-LOCK'), { params: { recordId } }),
    verifyMeasurementRecord: (recordId: string) =>
      call<unknown>(endpoint('API-ATELIER-MEAS-VERIFY'), { params: { recordId } }),
    atelierCloths: () => call<unknown>(endpoint('API-ATELIER-CLOTH-LIST')),
    atelierCloth: (clothId: string) =>
      call<unknown>(endpoint('API-ATELIER-CLOTH-GET'), { params: { clothId } }),
    moveStock: (clothId: string, body: StockMoveRequest) =>
      call<unknown>(endpoint('API-ATELIER-STOCK-MOVE'), { params: { clothId }, body }),
    setClothLifecycle: (
      clothId: string,
      body: { lifecycleState: 'published' | 'unpublished' | 'discontinued'; reason?: string },
    ) => call<unknown>(endpoint('API-ATELIER-CLOTH-LIFECYCLE'), { params: { clothId }, body }),
    swatchRequests: () => call<unknown>(endpoint('API-ATELIER-SWATCHES-LIST')),
    markSwatchSent: (id: string) =>
      call<unknown>(endpoint('API-ATELIER-SWATCHES-MARKSENT'), { params: { id } }),
    clothInterests: () => call<unknown>(endpoint('API-ATELIER-CLOTH-INTERESTS')),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * DELIBERATELY ABSENT, and it must stay that way:
 *   updateMeasurementRecord()  deleteMeasurementRecord()  setOrderState()
 *   setStockOnHand()           releaseConstraint()        submitPrice()
 * D-009, OPS_SPEC §2.6, OPS_SPEC §5.7.5, P-CFG-04, P-ORD-02.
 */
